import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { SESSION_EXPIRED_EVENT } from '@/shared/api/baseQuery';
import { getAccessToken } from '@/shared/lib/tokenStorage';

import { selectIsAuthenticated, signedOut } from './auth.slice';

/**
 * Whether the director may see protected screens.
 *
 * Both halves matter. The flag alone would survive a token cleared by a failed
 * refresh in another tab; the token alone would let someone who has logged in
 * but not yet passed OTP straight through.
 */
export const useIsAuthenticated = (): boolean => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  return isAuthenticated && getAccessToken() !== null;
};

/**
 * Reacts to a session the middleware could not save.
 *
 * The middleware runs outside React and cannot navigate, so it announces the
 * expiry on `window` and this hook — mounted once, in the shell — turns that
 * into a redirect. Mounting it twice would fire two navigations.
 */
export const useSessionExpiryRedirect = (): void => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const onExpired = (): void => {
      dispatch(
        signedOut('Your session has expired. Please use the link from your email again.'),
      );
      void navigate('/logout', { replace: true });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, [dispatch, navigate]);
};
